import { useEffect, useRef } from 'react';

import { ColumnLike, writeSessionColumnState } from '../utils/sessionColumnState';

/** Debounce window — a resize drag fires many width updates per second; this
 *  coalesces them into one write shortly after the user stops moving. */
const PERSIST_DELAY_MS = 250;

/**
 * Persist a list page's column layout (order / visibility / widths) to the
 * browser session, **debounced** so a resize drag doesn't serialize + write on
 * every pointer-move frame — it writes once when the drag settles. Any pending
 * write is flushed on unmount so a drag-then-navigate doesn't lose the final
 * layout.
 *
 * @param pageName  session key for the page (e.g. "ServiceList")
 * @param columns   current column defs
 * @param enabled   persist only when true — skipped while a saved report is
 *                  active, since that layout belongs to the report, not the session.
 */
export const useSessionColumnPersist = <T extends ColumnLike>(
  pageName: string,
  columns: T[],
  enabled: boolean
): void => {
  // Latest values for the unmount flush, without making it re-subscribe.
  const latest = useRef({ pageName, columns, enabled });
  latest.current = { pageName, columns, enabled };

  // Debounced write: each change resets the timer, so mid-drag frames never
  // write — only the final state does, ~PERSIST_DELAY_MS after the last change.
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(() => writeSessionColumnState(pageName, columns), PERSIST_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pageName, columns, enabled]);

  // Flush on unmount so a layout change made within the debounce window (e.g.
  // release then immediately navigate away) is still persisted.
  useEffect(
    () => () => {
      const { pageName: p, columns: c, enabled: e } = latest.current;
      if (e) writeSessionColumnState(p, c);
    },
    []
  );
};
